import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "../styleAllBlogs.css";

import sunkenWalkway from "../../../assets/img/sunken-concrete.jpeg";
import concreteReplacement from "../../../assets/img/sunken-concrete-fixed.jpeg";
import paverPathway from "../../../assets/img/paver-pathway.jpeg";
import foamLifting from "../../../assets/img/foam-lift.jpeg";
import mudjacking from "../../../assets/img/mud-jacking.jpeg";

export default function SunkenConcreteWalkwayOptions() {
  const location = useLocation();

  // Meta Tag changes
  useEffect(() => {
    const canonicalUrl = `https://bur-han.ca${location.pathname}`;
    const linkTag = document.querySelector('link[rel="canonical"]');
    if (linkTag) {
      linkTag.setAttribute("href", canonicalUrl);
    } else {
      const newLinkTag = document.createElement("link");
      newLinkTag.setAttribute("rel", "canonical");
      newLinkTag.setAttribute("href", canonicalUrl);
      document.head.appendChild(newLinkTag);
    }

    const newTitle = "Best Repair Options for Sunken Concrete Walkways | Expert Guide";
    document.title = newTitle;

    const newDescription = "Discover the best solutions for sunken concrete walkways: replacement, pavers, foam lifting, or mudjacking. Learn pros, cons, and costs for each method.";
    const descriptionMetaTag = document.querySelector(
      'meta[name="description"]'
    );
    if (descriptionMetaTag) {
      descriptionMetaTag.setAttribute("content", newDescription);
    } else {
      const newMetaTag = document.createElement("meta");
      newMetaTag.setAttribute("name", "description");
      newMetaTag.setAttribute("content", newDescription);
      document.head.appendChild(newMetaTag);
    }

    const newKeywords = [
      "sunken concrete repair",
      "concrete walkway replacement",
      "paver pathway installation",
      "polyurethane foam lifting",
      "mudjacking Vancouver",
      "concrete leveling",
      "uneven walkway solutions",
      "concrete repair options",
      "cost to fix sunken concrete",
      "best concrete lifting method",
      "walkway repair North Vancouver",
      "concrete vs pavers cost",
      "foam jacking vs mudjacking",
      "prevent concrete settling",
      "BUR-HAN concrete repair"
    ].join(", ");
    const keywordsMetaTag = document.querySelector('meta[name="keywords"]');
    if (keywordsMetaTag) {
      keywordsMetaTag.setAttribute("content", newKeywords);
    } else {
      const newMetaTag = document.createElement("meta");
      newMetaTag.setAttribute("name", "keywords");
      newMetaTag.setAttribute("content", newKeywords);
      document.head.appendChild(newMetaTag);
    }

    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (ogTitleTag) {
      ogTitleTag.setAttribute("content", newTitle);
    } else {
      const newOgTitleTag = document.createElement("meta");
      newOgTitleTag.setAttribute("property", "og:title");
      newOgTitleTag.setAttribute("content", newTitle);
      document.head.appendChild(newOgTitleTag);
    }

    const ogDescriptionTag = document.querySelector(
      'meta[property="og:description"]'
    );
    if (ogDescriptionTag) {
      ogDescriptionTag.setAttribute("content", newDescription);
    } else {
      const newOgDescriptionTag = document.createElement("meta");
      newOgDescriptionTag.setAttribute("property", "og:description");
      newOgDescriptionTag.setAttribute("content", newDescription);
      document.head.appendChild(newOgDescriptionTag);
    }
  }, [location.pathname]);

  return (
    <div className="wrapper">
      <Header />
      <div className="beforeSection"></div>
      <div className="sectionBlogpost sunkenConcrete">
        <a href="/blog" className="backPosts">
          <i className="bi bi-arrow-left"></i>
          Back
        </a>
        <article>
          <header>
            <h1>
              <div className="nth-line-1" data-aos="fade-up">
                What Are Your Best Options
              </div>
              <div className="nth-line-2" data-aos="fade-up">
                When You Have a Sunken Concrete Walkway?
              </div>
            </h1>
            <p className="lead" data-aos="fade-right"></p>
          </header>
          <hr />
          <section data-aos="fade-right">
            <p>
              A sunken concrete walkway isn't just unsightly—it's unsafe. Uneven slabs can cause trips, 
              pooling water, and a general decline in curb appeal. Whether caused by shifting soil, 
              moisture, or age, there are several proven options to repair or replace your walkway, 
              depending on the condition of the slab and your desired outcome.
            </p>
            
            <img loading="lazy"
              src={sunkenWalkway}
              alt="Uneven and sunken concrete walkway needing repair"
              title="Damaged Sunken Concrete Walkway"
              className="img-responsive"
            />

            <h2>Option 1: Full Concrete Replacement</h2>
            <p>
              When the concrete is cracked, broken, or the base underneath has failed, a full replacement 
              is often the best choice. This involves removing the existing slab, correcting the grade, 
              compacting a new base, and pouring fresh concrete.
            </p>
            <p>
              <span>Pros:</span>
              <ul>
                <li>Clean, uniform finish</li>
                <li>Long-lasting and durable</li>
                <li>Can address foundational issues underneath</li>
              </ul>
              <span>Cons:</span>
              <ul>
                <li>Most expensive option</li>
                <li>Weather-dependent installation</li>
                <li>Longer timeline due to curing time</li>
              </ul>
              Concrete replacement is the go-to option when both the surface and structure have deteriorated beyond repair.
            </p>
            <img loading="lazy"
              src={concreteReplacement}
              alt="New concrete walkway being installed"
              title="Concrete Walkway Replacement"
              className="img-responsive"
            />

            <h2>Option 2: Install a New Paver Pathway</h2>
            <p>
              Instead of pouring new concrete, you may opt for a paver stone walkway. Pavers offer a highly 
              customizable and attractive alternative with improved drainage and easier repairs. If a paver 
              ever becomes uneven or damaged, it can be replaced individually without removing the entire surface.
            </p>
            <p>
              <span>Pros:</span>
              <ul>
                <li>Visually appealing and customizable styles</li>
                <li>Easier to repair or adjust in future</li>
                <li>Excellent drainage performance</li>
                <li>No curing time – ready for use right after installation</li>
              </ul>
              <span>Cons:</span>
              <ul>
                <li>Slightly higher labor cost than concrete</li>
                <li>Requires precise base preparation to prevent shifting</li>
              </ul>
              <img loading="lazy"
              src={paverPathway}
              alt="Beautiful new paver stone walkway installation"
              title="Paver Pathway Alternative"
              className="img-responsive"
            />
              Pavers are ideal for homeowners looking to add beauty and flexibility to their landscape while solving the issue of uneven ground.
            </p>
           

            <h2>Option 3: Polyurethane Foam Lifting</h2>
            <p>
              When the slab is still in good shape but has sunk unevenly, polyurethane foam lifting is an 
              efficient and non-invasive solution. Foam is injected through small holes to lift the slab 
              back into position with remarkable accuracy.
            </p>
            <p>
              <span>Pros:</span>
              <ul>
                <li>Quick installation and curing (often walkable within minutes)</li>
                <li>Minimal disruption</li>
                <li>Lightweight material reduces risk of future settling</li>
                <li>Ideal for wet climates like Vancouver</li>
              </ul>
              <span>Cons:</span>
              <ul>
                <li>Not suitable for severely cracked or damaged concrete</li>
                <li>May cost more than mudjacking, but typically less than replacement</li>
              </ul>
              This is a great middle-ground solution when the concrete is intact but has shifted out of place.
            </p>
            <img loading="lazy"
              src={foamLifting}
              alt="Technician performing polyurethane foam injection to lift concrete"
              title="Foam Lifting Concrete Repair"
              className="img-responsive"
            />

            <h2>Option 4: Mudjacking (Slabjacking)</h2>
            <p>
              Mudjacking is the traditional method of lifting sunken concrete by pumping a cement slurry underneath. 
              It works well in certain applications but has more limitations in wet climates.
            </p>
            <p>
              <span>Pros:</span>
              <ul>
                <li>Lower cost than full replacement</li>
                <li>Can be effective for heavy or commercial slabs</li>
              </ul>
              <span>Cons:</span>
              <ul>
                <li>Larger drill holes</li>
                <li>Heavier material can contribute to future settling</li>
                <li>Less precise than foam</li>
                <li>Slurry can wash out over time</li>
              </ul>
              Mudjacking may still work in some cases, but in our wet West Coast environment, foam lifting tends to be a better long-term fix.
            </p>
            <img loading="lazy"
              src={mudjacking}
              alt="Mudjacking process for concrete slab lifting"
              title="Traditional Mudjacking Method"
              className="img-responsive"
            />

            <h2>Not Sure Which Option Is Right?</h2>
            <p>
              At BUR-HAN Garden & Lawn Care, we regularly partner with professionals like <a href="http://truelevelconcrete.ca/" style={{color:"var(--yellow-color)"}}>TrueLevel Concrete </a>
              to evaluate walkways, patios, and hard surfaces before jumping into repairs or landscaping. 
              If you're dealing with a sunken concrete walkway, let's help you determine the best solution—whether 
              that's rebuilding, replacing with pavers, or lifting what's already there.
            </p>
        

          </section>
        </article>
        <div className="relatedArticles">
          <h3>Related Articles</h3>
          <ul>
            <li><a href="/blog/expert-walkway-installation-in-greater-vancouver-a-complete-guide">Expert Walkway Installation in Greater Vancouver</a></li>
            <li><a href="/blog/replacing-a-timber-wall-with-an-allan-block-wall">Replacing a Timber Wall with an Allan Block Wall</a></li>
            <li><a href="/blog/walkway-installation-services-enhance-your-property">Walkway Installation Services</a></li>
          </ul>
        </div>
        <div className="secondBanner">
          <div className="container" data-aos="fade-down">
            <div className="row">
              <div className="col-md-6">
                <h3>Ready to restore your walkway?</h3>
                <p>
                  Contact us to explore the safest and most cost-effective path forward for your property.
                </p>
              </div>
              <div className="col-md-6 ">
                <div className="call-to-action text-right">
                  <a href="/contact-us" className="get-a-quote">
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}